import React, { useState, useEffect } from "react";
import { 
  Plus, Edit3, Trash2, RefreshCw, FileText, ToggleLeft, ToggleRight,
  ArrowUpDown, ChevronDown, AlertCircle, MessageCircle, Save
} from "lucide-react";
import api from "../../services/api";
import { Modal, Button, Input, Select, Card, Table, message, Tag, Space, Switch, Tooltip, Popconfirm, Tabs } from "antd";

export default function AdminCrmQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    question_key: "",
    question_text_fr: "",
    question_text_en: "",
    question_text_es: "",
    question_type: "text",
    options: [],
    display_order: 0,
    step: 2,
    is_required: true,
    is_active: true,
    is_standard: false,
  });
  const [optionsStr, setOptionsStr] = useState("");

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/crm/questions");
      if (res.success) setQuestions(res.data);
    } catch (err) {
      message.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQuestions(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      question_key: "", question_text_fr: "", question_text_en: "", question_text_es: "",
      question_type: "text", options: [], display_order: 0, step: 2,
      is_required: true, is_active: true, is_standard: false,
    });
    setOptionsStr("");
    setModal(true);
  };

  const openEdit = (q) => {
    setEditing(q);
    setForm({ ...q });
    setOptionsStr(q.options ? q.options.join("\n") : "");
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.question_key || !form.question_text_fr) {
      message.error("Veuillez remplir les champs obligatoires");
      return;
    }

    const payload = {
      ...form,
      options: form.question_type === 'select' || form.question_type === 'radio' 
        ? optionsStr.split('\n').filter(o => o.trim()) 
        : null,
    };

    try {
      if (editing) {
        await api.put(`/admin/crm/questions/${editing.id}`, payload);
        message.success("Question mise à jour");
      } else {
        await api.post("/admin/crm/questions", payload);
        message.success("Question créée");
      }
      setModal(false);
      loadQuestions();
    } catch (err) {
      message.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/crm/questions/${id}`);
      message.success("Question supprimée");
      loadQuestions();
    } catch (err) {
      message.error("Erreur lors de la suppression");
    }
  };

  const toggleActive = async (q) => {
    try {
      await api.put(`/admin/crm/questions/${q.id}`, { is_active: !q.is_active });
      message.success(q.is_active ? "Question désactivée" : "Question activée");
      loadQuestions();
    } catch (err) {
      message.error("Erreur");
    }
  };

  const columns = [
    { 
      title: "Key", 
      dataIndex: "question_key", 
      key: "question_key",
      render: (val) => <code className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">{val}</code>,
    },
    { 
      title: "Question (FR)", 
      dataIndex: "question_text_fr", 
      key: "question_text_fr",
      render: (val, record) => (
        <div>
          <div className="text-sm text-white font-medium">{val}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">EN: {record.question_text_en || '—'}</div>
        </div>
      ),
    },
    { 
      title: "Type", 
      dataIndex: "question_type", 
      key: "question_type",
      render: (val) => <Tag>{val}</Tag>,
    },
    { 
      title: "Étape", 
      dataIndex: "step", 
      key: "step",
      render: (val) => <Tag color="purple">Étape {val}</Tag>,
    },
    { 
      title: "Ordre", 
      dataIndex: "display_order", 
      key: "display_order",
      render: (val) => <span className="text-xs text-slate-400">{val}</span>,
    },
    { 
      title: "Requis", 
      dataIndex: "is_required", 
      key: "is_required",
      render: (val) => val ? <Tag color="green">Oui</Tag> : <Tag>Non</Tag>,
    },
    { 
      title: "Standard", 
      dataIndex: "is_standard", 
      key: "is_standard",
      render: (val) => val ? <Tag color="blue">Système</Tag> : <Tag>Custom</Tag>,
    },
    { 
      title: "Actif", 
      dataIndex: "is_active", 
      key: "is_active",
      render: (_, record) => (
        <Switch 
          checked={record.is_active} 
          onChange={() => toggleActive(record)}
          size="small"
        />
      ),
    },
    { 
      title: "Actions", 
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button type="text" icon={<Edit3 size={14} />} onClick={() => openEdit(record)} className="text-slate-400 hover:text-white" size="small" />
          </Tooltip>
          {!record.is_standard && (
            <Popconfirm title="Supprimer cette question ?" onConfirm={() => handleDelete(record.id)} okText="Oui" cancelText="Non">
              <Tooltip title="Supprimer">
                <Button type="text" icon={<Trash2 size={14} />} className="text-slate-400 hover:text-red-400" size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Charger le numéro WhatsApp
  const loadWhatsAppNumber = async () => {
    setWhatsappLoading(true);
    try {
      const res = await api.get("/admin/crm/whatsapp-settings");
      if (res.success) {
        setWhatsappNumber(res.data.whatsapp_number || "");
      }
    } catch (err) {
      console.error("Erreur chargement WhatsApp settings:", err);
    } finally {
      setWhatsappLoading(false);
    }
  };

  useEffect(() => { loadWhatsAppNumber(); }, []);

  const handleSaveWhatsApp = async () => {
    if (!whatsappNumber) {
      message.error("Veuillez entrer un numéro WhatsApp");
      return;
    }
    setSavingWhatsapp(true);
    try {
      const res = await api.put("/admin/crm/whatsapp-settings", { whatsapp_number: whatsappNumber });
      if (res.success) {
        message.success("Numéro WhatsApp mis à jour avec succès");
      }
    } catch (err) {
      message.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const tabItems = [
    {
      key: "questions",
      label: (
        <span className="flex items-center gap-2">
          <FileText size={14} />
          Questions
        </span>
      ),
      children: (
        <>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Questions du Formulaire CRM</h1>
              <p className="text-slate-400 text-sm mt-1">Gérez les questions posées aux prospects lors de l'inscription</p>
            </div>
            <Space>
              <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
                Ajouter une Question
              </Button>
              <Button icon={<RefreshCw size={14} />} onClick={loadQuestions}>
                Actualiser
              </Button>
            </Space>
          </div>

          <Card className="bg-slate-900 border-white/5" bodyStyle={{ padding: 0 }}>
            <Table 
              columns={columns}
              dataSource={questions}
              rowKey="id"
              loading={loading}
              pagination={false}
              className="crm-questions-table"
              locale={{ emptyText: "Aucune question définie" }}
            />
          </Card>
        </>
      ),
    },
    {
      key: "whatsapp",
      label: (
        <span className="flex items-center gap-2">
          <MessageCircle size={14} />
          WhatsApp
        </span>
      ),
      children: (
        <div className="max-w-2xl mx-auto mt-4">
          <Card className="bg-slate-900 border-white/5">
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Configuration WhatsApp</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Configurez le numéro WhatsApp qui recevra les notifications des nouveaux leads.
                  Les prospects seront invités à contacter ce numéro.
                </p>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MessageCircle size={20} className="text-emerald-400 mt-0.5" />
                  <div>
                    <p className="text-emerald-300 text-sm font-medium">Recommandation</p>
                    <p className="text-emerald-400/70 text-xs mt-1">
                      Nous recommandons d'utiliser un numéro WhatsApp Business pour une gestion professionnelle des leads.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Numéro WhatsApp (format international)
                </label>
                <div className="flex gap-3">
                  <Input 
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    placeholder="ex: 212600000000"
                    size="large"
                    className="flex-1"
                    prefix="+"
                  />
                  <Button 
                    type="primary"
                    icon={<Save size={14} />}
                    onClick={handleSaveWhatsApp}
                    loading={savingWhatsapp}
                  >
                    Enregistrer
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Format: indicatif pays + numéro (ex: 212 pour Maroc, 33 pour France)
                </p>
              </div>

              <div className="border-t border-white/5 pt-4">
                <h3 className="text-sm font-semibold text-white mb-2">Test du numéro</h3>
                <p className="text-xs text-slate-400 mb-3">
                  Cliquez sur le bouton ci-dessous pour tester le lien WhatsApp
                </p>
                <a
                  href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=Test%20CRM%20Ennnovatrade`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm hover:bg-emerald-500/20 transition-colors"
                >
                  <MessageCircle size={16} />
                  Tester le lien WhatsApp
                </a>
              </div>
            </div>
          </Card>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6">
      <Tabs defaultActiveKey="questions" items={tabItems} className="crm-tabs-premium" />

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? "Modifier la question" : "Nouvelle question"}
        open={modal}
        onCancel={() => setModal(false)}
        onOk={handleSave}
        okText={editing ? "Mettre à jour" : "Créer"}
        width={650}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Question Key *</label>
              <Input 
                value={form.question_key}
                onChange={(e) => setForm(prev => ({ ...prev, question_key: e.target.value.replace(/\s/g, '_').toLowerCase() }))}
                placeholder="ex: votre_question"
                disabled={editing}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Type *</label>
              <Select 
                value={form.question_type} 
                onChange={(val) => setForm(prev => ({ ...prev, question_type: val }))}
                style={{ width: '100%' }}
              >
                <Select.Option value="text">Texte court</Select.Option>
                <Select.Option value="textarea">Texte long</Select.Option>
                <Select.Option value="select">Sélection</Select.Option>
                <Select.Option value="radio">Choix unique</Select.Option>
                <Select.Option value="checkbox">Cases à cocher</Select.Option>
                <Select.Option value="number">Nombre</Select.Option>
                <Select.Option value="email">Email</Select.Option>
                <Select.Option value="tel">Téléphone</Select.Option>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Question (FR) *</label>
            <Input 
              value={form.question_text_fr}
              onChange={(e) => setForm(prev => ({ ...prev, question_text_fr: e.target.value }))}
              placeholder="Texte de la question en français"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Question (EN)</label>
              <Input 
                value={form.question_text_en}
                onChange={(e) => setForm(prev => ({ ...prev, question_text_en: e.target.value }))}
                placeholder="Question in English"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Question (ES)</label>
              <Input 
                value={form.question_text_es}
                onChange={(e) => setForm(prev => ({ ...prev, question_text_es: e.target.value }))}
                placeholder="Pregunta en español"
              />
            </div>
          </div>

          {/* Options pour select/radio/checkbox */}
          {(form.question_type === 'select' || form.question_type === 'radio' || form.question_type === 'checkbox') && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Options (une par ligne)</label>
              <Input.TextArea 
                value={optionsStr}
                onChange={(e) => setOptionsStr(e.target.value)}
                rows={4}
                placeholder={`Option 1\nOption 2\nOption 3`}
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Étape</label>
              <Select value={form.step} onChange={(val) => setForm(prev => ({ ...prev, step: val }))} style={{ width: '100%' }}>
                <Select.Option value={1}>Étape 1 - Infos</Select.Option>
                <Select.Option value={2}>Étape 2 - Évaluation</Select.Option>
                <Select.Option value={3}>Étape 3 - Contact</Select.Option>
              </Select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Ordre d'affichage</label>
              <Input 
                type="number" 
                value={form.display_order} 
                onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))} 
              />
            </div>
            <div className="space-y-3 pt-5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Obligatoire</span>
                <Switch checked={form.is_required} onChange={(val) => setForm(prev => ({ ...prev, is_required: val }))} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Active</span>
                <Switch checked={form.is_active} onChange={(val) => setForm(prev => ({ ...prev, is_active: val }))} />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        .crm-questions-table .ant-table-thead > tr > th {
          background: #0f172a !important;
          border-bottom: 1px solid rgba(255,255,255,0.06) !important;
          color: #94a3b8 !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          padding: 12px 16px !important;
        }
        .crm-questions-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(255,255,255,0.04) !important;
          padding: 12px 16px !important;
          background: transparent !important;
        }
        .crm-questions-table .ant-table-tbody > tr:hover > td {
          background: rgba(99,102,241,0.04) !important;
        }
      `}</style>
    </div>
  );
}
